import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { supabase } from "@/lib/supabaseClient";
import { isYouTubeUrl, normalizeYouTubeUrl, extractYouTubeId } from "@/lib/youtube";
import {
  ArrowLeft, Plus, Trash2, GripVertical, Upload,
  Video, X, Check, AlertCircle, Loader2, AlertTriangle,
} from "lucide-react";
import { translateError } from "@/lib/errors";

// ── Styling ──────────────────────────────────────────────────────────────────

const inp = "w-full bg-[#111] border border-white/10 text-[#F5F5F7] text-sm px-3 py-1.5 focus:outline-none focus:border-[#C5A059] transition-colors placeholder-[#2a2a2a]";
const ta  = `${inp} resize-y`;
const sel = `${inp} cursor-pointer`;

// ── Tabs ─────────────────────────────────────────────────────────────────────

const TABS = [
  { id: "temel",  label: "Temel"        },
  { id: "acik",   label: "Açıklamalar"  },
  { id: "medya",  label: "Medya"        },
  { id: "galeri", label: "Galeri"       },
  { id: "ozel",   label: "Özellikler"   },
  { id: "malz",   label: "Malzeme"      },
  { id: "teknik", label: "Teknik"       },
  { id: "yakin",  label: "Yakın Çevre"  },
  { id: "kat",    label: "Kat Planları" },
  { id: "seo",    label: "SEO"          },
  { id: "harita", label: "Harita"       },
];

// ── Default values ────────────────────────────────────────────────────────────

const DEFAULTS = {
  title: "", slug: "", status: "completed", sort_order: 10,
  is_published: true, location: "", year: "", units: "",
  area: "", type: "", address: "",
  short_description: "", description: "",
  cover_image: "", cover_video: "", cover_video_poster: "",
  seo_title: "", seo_description: "",
  lat: "", lng: "",
  features: [], gallery: [], materialHighlights: [], nearby: [],
  floorPlans: [],
  technicalDetails: {
    constructionArea: "", greenArea: "", blocks: "",
    shopCount: "", floors: "", delivery: "",
  },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const toSlug = (t) =>
  t.toLowerCase()
    .replace(/ğ/g,"g").replace(/ü/g,"u").replace(/ş/g,"s")
    .replace(/ı/g,"i").replace(/ö/g,"o").replace(/ç/g,"c")
    .replace(/[^a-z0-9\s-]/g,"").replace(/\s+/g,"-").replace(/-+/g,"-").trim();

const upload = async (file, folder = "uploads") => {
  if (!supabase) throw new Error("Supabase bağlantısı yapılandırılmamış.");
  const ext  = file.name.split(".").pop().toLowerCase();
  const name = `${Date.now()}_${Math.random().toString(36).slice(2,8)}.${ext}`;
  const { error } = await supabase.storage
    .from("project-assets")
    .upload(`${folder}/${name}`, file);
  if (error) throw error;
  const { data } = supabase.storage.from("project-assets").getPublicUrl(`${folder}/${name}`);
  return data.publicUrl;
};

// Normalize any YouTube URL; leave non-YouTube unchanged.
const normalizeVideo = (url = "") =>
  isYouTubeUrl(url) ? normalizeYouTubeUrl(url) : url;

// Validation: YouTube URL must have a valid 11-char ID
const youtubeWarning = (url = "") => {
  if (!url) return null;
  if (!isYouTubeUrl(url)) return null;
  return extractYouTubeId(url) ? null : "Geçersiz YouTube URL'si — video kimliği bulunamadı.";
};

const normGallery = (arr = []) =>
  arr.map(g =>
    typeof g === "string"
      ? { type: "image", src: g, poster: "", thumb: "", caption: "" }
      : { type: g.type||"image", src: g.src||"", poster: g.poster||"", thumb: g.thumb||"", caption: g.caption||"" }
  );

const serGallery = (arr = []) =>
  arr.map(g => {
    const src = g.type === "youtube" ? normalizeYouTubeUrl(g.src) : g.src;
    if (g.type === "image" && !g.poster && !g.thumb && !g.caption) return src;
    return {
      type: g.type, src,
      ...(g.poster  && { poster:  g.poster  }),
      ...(g.thumb   && { thumb:   g.thumb   }),
      ...(g.caption && { caption: g.caption }),
    };
  });

const isImgUrl = (v = "") => /\.(png|jpg|jpeg|webp|gif|svg|avif)(\?|$)/i.test(v);

// ── Shared primitives ─────────────────────────────────────────────────────────

const Lbl = ({ children }) => (
  <p className="text-[10px] tracking-widest text-[#666] uppercase mb-1">{children}</p>
);

const SecTitle = ({ children }) => (
  <p className="text-[#C5A059] text-[10px] tracking-[0.25em] uppercase mb-4 pb-2 border-b border-white/5">
    {children}
  </p>
);

const Warn = ({ text }) =>
  text ? (
    <div className="flex items-center gap-1.5 mt-1 text-amber-400 text-xs">
      <AlertTriangle size={11} />
      {text}
    </div>
  ) : null;

// ── Drag handle wrapper ───────────────────────────────────────────────────────

const DragItem = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 }}
      className="flex gap-1.5 items-center"
    >
      <button
        type="button" {...attributes} {...listeners}
        className="p-1 text-[#222] hover:text-[#444] cursor-grab active:cursor-grabbing flex-shrink-0"
      >
        <GripVertical size={13} />
      </button>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
};

const endDrag = (e, fields, move) => {
  const { active, over } = e;
  if (!over || active.id === over.id) return;
  const from = fields.findIndex(f => f.id === active.id);
  const to   = fields.findIndex(f => f.id === over.id);
  if (from >= 0 && to >= 0) move(from, to);
};

// ── Drop zone ─────────────────────────────────────────────────────────────────

const DropZone = ({ value, onFile, onClear, accept = "image/*", hint, uploading, compact }) => {
  const [over, setOver] = useState(false);
  const ref = useRef();
  const isImg = isImgUrl(value || "");
  const isYT  = isYouTubeUrl(value || "");

  const handle = (files) => { if (files[0] && onFile) onFile(files[0]); };

  return (
    <div
      onDragOver={e => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={e => { e.preventDefault(); setOver(false); handle(Array.from(e.dataTransfer.files)); }}
      className={`relative border border-dashed transition-colors ${
        over ? "border-[#C5A059] bg-[#C5A059]/5" : "border-white/8 hover:border-white/15"
      }`}
    >
      {value ? (
        <>
          {isImg ? (
            <img src={value} alt="" className={`w-full object-cover ${compact ? "max-h-20" : "max-h-40"}`}
              onError={e => { e.target.style.display = "none"; }} />
          ) : (
            <div className={`flex items-center gap-2 px-3 ${compact ? "h-12" : "h-16"}`}>
              {isYT ? <span className="text-red-400 text-xs">▶ YouTube</span>
                    : <Video size={13} className="text-[#555]" />}
              <span className="text-[#333] text-xs truncate max-w-[220px]">
                {isYT ? value : value.split("/").pop()}
              </span>
            </div>
          )}
          <button type="button" onClick={onClear}
            className="absolute top-1 right-1 w-5 h-5 bg-black/70 flex items-center justify-center hover:bg-red-500/70 transition-colors">
            <X size={9} />
          </button>
        </>
      ) : (
        <label className={`flex flex-col items-center justify-center gap-1 cursor-pointer ${compact ? "py-3" : "py-6"}`}>
          {uploading
            ? <Loader2 size={15} className="text-[#C5A059] animate-spin" />
            : <Upload size={15} className="text-[#333]" />}
          <span className="text-xs text-[#444]">
            {uploading ? "Yükleniyor..." : "Sürükle veya tıkla"}
          </span>
          {hint && <span className="text-[11px] text-[#2a2a2a]">{hint}</span>}
          <input ref={ref} type="file" accept={accept} className="sr-only"
            onChange={e => handle(Array.from(e.target.files))} />
        </label>
      )}
    </div>
  );
};

// ── Floor plan group ──────────────────────────────────────────────────────────

const FloorPlanGroup = ({ gi, control, register, watch, setValue, removeGroup, folder }) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const { fields: plans, append, remove, move } = useFieldArray({
    control, name: `floorPlans.${gi}.plans`,
  });
  const [upIdx, setUpIdx] = useState(null);

  return (
    <div className="bg-[#0d0d0d] border border-white/5 p-3 space-y-2">
      <div className="flex gap-2 items-center">
        <input
          {...register(`floorPlans.${gi}.typeTitle`)}
          className={`${inp} flex-1 text-xs`}
          placeholder="3+1 Villa Tipi"
        />
        <button type="button" onClick={removeGroup}
          className="p-1.5 text-[#333] hover:text-red-400 transition-colors flex-shrink-0">
          <Trash2 size={12} />
        </button>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter}
        onDragEnd={e => endDrag(e, plans, move)}>
        <SortableContext items={plans.map(p => p.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-1.5">
            {plans.map((plan, pi) => {
              const imgField = `floorPlans.${gi}.plans.${pi}.image`;
              const imgVal   = watch(imgField) || "";
              return (
                <DragItem key={plan.id} id={plan.id}>
                  <div className="bg-[#111] border border-white/5 p-2 grid grid-cols-[80px_1fr_auto] gap-2 items-start">
                    {/* thumbnail / upload */}
                    <div>
                      {imgVal && isImgUrl(imgVal)
                        ? <img src={imgVal} alt="" className="w-full h-14 object-cover border border-white/5"
                            onError={() => {}} />
                        : (
                          <label className="flex flex-col items-center justify-center h-14 border border-dashed border-white/8 cursor-pointer hover:border-white/15 transition-colors">
                            {upIdx === pi
                              ? <Loader2 size={12} className="text-[#C5A059] animate-spin" />
                              : <Upload size={12} className="text-[#333]" />}
                            <span className="text-[10px] text-[#333] mt-0.5">Yükle</span>
                            <input type="file" accept="image/*" className="sr-only"
                              onChange={async (e) => {
                                const f = e.target.files[0];
                                if (!f) return;
                                setUpIdx(pi);
                                try {
                                  const url = await upload(f, folder);
                                  setValue(imgField, url, { shouldDirty: true });
                                } catch (err) { setSaveMsg({ ok: false, text: translateError(err) }); }
                                setUpIdx(null);
                                e.target.value = "";
                              }} />
                          </label>
                        )
                      }
                      {imgVal && (
                        <button type="button"
                          onClick={() => setValue(imgField, "", { shouldDirty: true })}
                          className="w-full text-[10px] text-[#333] hover:text-red-400 mt-0.5 transition-colors">
                          Sil
                        </button>
                      )}
                    </div>
                    {/* fields */}
                    <div className="space-y-1.5">
                      <input
                        {...register(imgField)}
                        className={`${inp} text-xs`}
                        placeholder="URL veya /images/..."
                      />
                      <textarea
                        {...register(`floorPlans.${gi}.plans.${pi}.label`)}
                        className={`${ta} text-xs`}
                        rows={2}
                        placeholder={"Zemin Kat\n• Salon 30m²"}
                      />
                    </div>
                    {/* delete */}
                    <button type="button" onClick={() => remove(pi)}
                      className="p-1 text-[#333] hover:text-red-400 transition-colors mt-0.5">
                      <Trash2 size={11} />
                    </button>
                  </div>
                </DragItem>
              );
            })}
          </div>
        </SortableContext>
      </DndContext>

      <button type="button" onClick={() => append({ image: "", label: "" })}
        className="flex items-center gap-1 text-xs text-[#444] hover:text-[#C5A059] transition-colors">
        <Plus size={11} /> Plan Ekle
      </button>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const AdminProjectEdit = () => {
  const { id }     = useParams();
  const navigate   = useNavigate();
  const isNew      = id === "yeni";

  const [tab,           setTab]           = useState("temel");
  const [dataLoading,   setDataLoading]   = useState(!isNew);
  const [saving,        setSaving]        = useState(false);
  const [saveMsg,       setSaveMsg]       = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkProgress,  setBulkProgress]  = useState({ done: 0, total: 0 });
  const [coverImgUp,    setCoverImgUp]    = useState(false);
  const [coverVidUp,       setCoverVidUp]       = useState(false);
  const [coverVidPosterUp, setCoverVidPosterUp] = useState(false);
  const bulkRef = useRef();

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const { register, handleSubmit, watch, setValue, control, reset } = useForm({
    defaultValues: DEFAULTS,
  });

  const { fields: feats,    append: addFeat,    remove: remFeat,    move: movFeat    } = useFieldArray({ control, name: "features"           });
  const { fields: galItems, append: addGal,     remove: remGal,     move: movGal     } = useFieldArray({ control, name: "gallery"             });
  const { fields: mats,     append: addMat,     remove: remMat,     move: movMat     } = useFieldArray({ control, name: "materialHighlights"   });
  const { fields: nearbs,   append: addNear,    remove: remNear,    move: movNear    } = useFieldArray({ control, name: "nearby"              });
  const { fields: fpGroups, append: addFpGroup, remove: remFpGroup, move: movFpGroup } = useFieldArray({ control, name: "floorPlans"           });

  const titleVal    = watch("title");
  const currentSlug = watch("slug");
  const coverImg    = watch("cover_image");
  const coverVid    = watch("cover_video");
  const folder      = currentSlug || "uploads";

  useEffect(() => {
    if (isNew && titleVal) setValue("slug", toSlug(titleVal));
  }, [titleVal, isNew, setValue]);

  useEffect(() => {
    if (isNew) return;
    supabase.from("projects").select("*").eq("id", id).single()
      .then(({ data, error }) => {
        if (error || !data) { navigate("/admin/projeler", { replace: true }); return; }
        reset({
          title:             data.title             || "",
          slug:              data.slug              || "",
          status:            data.status            || "completed",
          sort_order:        data.sort_order        ?? 10,
          is_published:      data.is_published      ?? true,
          location:          data.location          || "",
          year:              data.year              || "",
          units:             data.units             || "",
          area:              data.area              || "",
          type:              data.type              || "",
          address:           data.address           || "",
          short_description: data.short_description || "",
          description:       data.description       || "",
          cover_image:       data.cover_image       || "",
          cover_video:        data.cover_video        || "",
          cover_video_poster: data.cover_video_poster || "",
          seo_title:         data.seo_title         || "",
          seo_description:   data.seo_description   || "",
          lat:               data.lat               ?? "",
          lng:               data.lng               ?? "",
          features:          (data.features || []).map(f => ({ value: typeof f === "string" ? f : String(f) })),
          gallery:           normGallery(data.gallery),
          materialHighlights:(data.material_highlights || []).map(m => ({ title: m.title||"", description: m.description||"" })),
          nearby:            (data.nearby || []).map(n => ({ value: typeof n === "string" ? n : String(n) })),
          floorPlans:        (data.floor_plans || []).map(g => ({
            typeTitle: g.typeTitle || "",
            plans:     (g.plans || []).map(p => ({ image: p.image||"", label: p.label||"" })),
          })),
          technicalDetails: {
            constructionArea: data.technical_details?.constructionArea || "",
            greenArea:        data.technical_details?.greenArea        || "",
            blocks:           data.technical_details?.blocks           || "",
            shopCount:        data.technical_details?.shopCount        || "",
            floors:           data.technical_details?.floors           || "",
            delivery:         data.technical_details?.delivery         || "",
          },
        });
        setDataLoading(false);
      });
  }, [id, isNew, navigate, reset]);

  const handleBulkUpload = useCallback(async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setBulkUploading(true);
    setBulkProgress({ done: 0, total: files.length });
    let done = 0;
    for (const file of files) {
      try {
        const url  = await upload(file, folder);
        const type = file.type.startsWith("video/") ? "video" : "image";
        addGal({ type, src: url, poster: "", thumb: "", caption: "" });
        done++;
        setBulkProgress({ done, total: files.length });
      } catch (err) { done--; /* saymayı geri al */ }
    }
    setBulkUploading(false);
    if (done < files.length) {
      const failed = files.length - done;
      setSaveMsg({ ok: false, text: `${failed} dosya yüklenemedi. İnternet bağlantınızı kontrol edip tekrar deneyin.` });
    }
    e.target.value = "";
  }, [folder, addGal]);

  const onSubmit = async (fd) => {
    setSaving(true);
    setSaveMsg(null);

    // sort_order for new projects: auto-assign MAX+1 to avoid unique constraint conflicts.
    // For existing projects: omitted from payload — managed via list drag-and-drop only.
    let newSortOrder;
    if (isNew) {
      const { data: maxRow } = await supabase
        .from("projects")
        .select("sort_order")
        .order("sort_order", { ascending: false })
        .limit(1)
        .single();
      newSortOrder = (maxRow?.sort_order ?? 0) + 1;
    }

    const payload = {
      title:             fd.title,
      slug:              fd.slug,
      status:            fd.status,
      ...(isNew ? { sort_order: newSortOrder } : {}),
      is_published:      fd.is_published,
      location:          fd.location          || null,
      year:              fd.year              || null,
      units:             fd.units             || null,
      area:              fd.area              || null,
      type:              fd.type              || null,
      address:           fd.address           || null,
      short_description: fd.short_description || null,
      description:       fd.description       || null,
      cover_image:       fd.cover_image       || null,
      cover_video:        normalizeVideo(fd.cover_video)  || null,
      cover_video_poster: fd.cover_video_poster          || null,
      seo_title:         fd.seo_title         || null,
      seo_description:   fd.seo_description   || null,
      lat:               fd.lat !== "" ? Number(fd.lat) : null,
      lng:               fd.lng !== "" ? Number(fd.lng) : null,
      features:          fd.features.map(f => f.value).filter(Boolean),
      gallery:           serGallery(fd.gallery),
      material_highlights: fd.materialHighlights.map(m => ({ title: m.title, description: m.description })),
      nearby:            fd.nearby.map(n => n.value).filter(Boolean),
      floor_plans:       fd.floorPlans.map(g => ({
        typeTitle: g.typeTitle,
        plans:     (g.plans || []).map(p => ({ image: p.image, label: p.label })),
      })),
      technical_details: {
        constructionArea: fd.technicalDetails.constructionArea,
        greenArea:        fd.technicalDetails.greenArea,
        blocks:           fd.technicalDetails.blocks,
        shopCount:        fd.technicalDetails.shopCount,
        floors:           fd.technicalDetails.floors,
        delivery:         fd.technicalDetails.delivery,
      },
      updated_at: new Date().toISOString(),
    };

    const { error } = isNew
      ? await supabase.from("projects").insert(payload)
      : await supabase.from("projects").update(payload).eq("id", id);

    setSaveMsg(error
      ? { ok: false, text: translateError(error) }
      : { ok: true,  text: "Kaydedildi." });
    setSaving(false);
    if (!error && isNew) navigate("/admin/projeler");
  };

  if (dataLoading) {
    return (
      <div className="flex items-center gap-2 text-[#555] text-sm">
        <Loader2 size={15} className="animate-spin text-[#C5A059]" />
        Yükleniyor...
      </div>
    );
  }

  // ── Tab content ───────────────────────────────────────────────────────────

  const renderTab = () => {
    switch (tab) {

      case "temel": return (
        <div className="space-y-3">
          <SecTitle>Temel Bilgiler</SecTitle>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Lbl>Proje Adı</Lbl>
              <input {...register("title", { required: true })} className={inp} placeholder="Saklı Bahçe Butik Villaları" />
            </div>
            <div>
              <Lbl>Slug (URL)</Lbl>
              <input {...register("slug", { required: true })} className={inp} placeholder="sakli-bahce-konya" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Lbl>Durum</Lbl>
              <select {...register("status")} className={sel}>
                <option value="completed">Tamamlandı</option>
                <option value="ongoing">Devam Ediyor</option>
                <option value="planning">Planlama</option>
              </select>
            </div>
            <div>
              <Lbl>Sıra</Lbl>
              <input
                type="number" min="1"
                {...register("sort_order", { valueAsNumber: true })}
                className={`${inp} ${!isNew ? "opacity-40 cursor-not-allowed" : ""}`}
                readOnly={!isNew}
              />
              {!isNew && (
                <p className="text-[#2a2a2a] text-[10px] mt-0.5">Listeden sürükleyerek değiştirin</p>
              )}
            </div>
            <div>
              <Lbl>Yayın</Lbl>
              <div className="flex items-center gap-2 h-8">
                <input type="checkbox" id="pub" {...register("is_published")} className="w-3.5 h-3.5 accent-[#C5A059]" />
                <label htmlFor="pub" className="text-sm text-[#F5F5F7] font-light cursor-pointer">Yayında</label>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Lbl>Lokasyon</Lbl>
              <select {...register("location")} className={sel}>
                <option value="">— Seçin —</option>
                <option value="Meram">Meram</option>
                <option value="Selçuklu">Selçuklu</option>
                <option value="Konya">Konya</option>
              </select>
            </div>
            <div><Lbl>Yıl</Lbl><input {...register("year")} className={inp} placeholder="2026" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Lbl>Birim</Lbl><input {...register("units")} className={inp} placeholder="8 Villa" /></div>
            <div><Lbl>Alan</Lbl><input {...register("area")} className={inp} placeholder="3.000 m²" /></div>
            <div>
              <Lbl>Tür</Lbl>
              <select {...register("type")} className={sel}>
                <option value="">— Seçin —</option>
                <option value="Villa">Villa</option>
                <option value="Apartman">Apartman</option>
                <option value="Rezidans">Rezidans</option>
                <option value="Kooperatif">Kooperatif</option>
                <option value="Ticari">Ticari</option>
                <option value="Site">Site</option>
              </select>
            </div>
          </div>
          <div><Lbl>Adres</Lbl><input {...register("address")} className={inp} placeholder="Hocacihan Hacılar, Konya" /></div>
        </div>
      );

      case "acik": return (
        <div className="space-y-3">
          <SecTitle>Açıklamalar</SecTitle>
          <div>
            <Lbl>Kısa Açıklama</Lbl>
            <textarea {...register("short_description")} className={ta} rows={3}
              placeholder="Meram'da butik villa konseptiyle lüks yaşam alanları." />
          </div>
          <div>
            <Lbl>Detaylı Açıklama</Lbl>
            <textarea {...register("description")} className={ta} rows={8}
              placeholder="Projenin detaylı açıklaması..." />
          </div>
        </div>
      );

      case "medya": return (
        <div className="space-y-4">
          <SecTitle>Kapak Görseli</SecTitle>
          {/* Fixed 16:6 preview container */}
          <Controller control={control} name="cover_image"
            render={({ field }) => {
              const hasImg = field.value && isImgUrl(field.value);
              return (
                <div>
                  <div className="relative w-full bg-[#111] border border-white/8" style={{ paddingTop: "37.5%" }}>
                    <div className="absolute inset-0">
                      {hasImg ? (
                        <>
                          <img
                            src={field.value}
                            alt=""
                            className="w-full h-full object-cover"
                            onError={e => { e.target.style.display = "none"; }}
                          />
                          <button type="button" onClick={() => field.onChange("")}
                            className="absolute top-1.5 right-1.5 w-6 h-6 bg-black/70 flex items-center justify-center hover:bg-red-500/80 transition-colors z-10">
                            <X size={10} />
                          </button>
                        </>
                      ) : (
                        <label className="flex flex-col items-center justify-center h-full cursor-pointer hover:bg-white/2 transition-colors">
                          {coverImgUp
                            ? <Loader2 size={16} className="text-[#C5A059] animate-spin" />
                            : <Upload size={16} className="text-[#333]" />}
                          <span className="text-xs text-[#444] mt-1">
                            {coverImgUp ? "Yükleniyor..." : "Görsel sürükle veya tıkla"}
                          </span>
                          <input type="file" accept="image/*" className="sr-only"
                            onChange={async e => {
                              const f = e.target.files[0];
                              if (!f) return;
                              setCoverImgUp(true);
                              try { field.onChange(await upload(f, folder)); }
                              catch (err) { setSaveMsg({ ok: false, text: translateError(err) }); }
                              setCoverImgUp(false);
                              e.target.value = "";
                            }} />
                        </label>
                      )}
                    </div>
                  </div>
                  <div className="mt-2">
                    <Lbl>Veya URL</Lbl>
                    <input {...register("cover_image")} className={inp} placeholder="/images/projeler/kapak.png" />
                  </div>
                </div>
              );
            }}
          />

          <SecTitle>Kapak Video (opsiyonel)</SecTitle>
          <p className="text-[#333] text-xs -mt-2">YouTube URL veya MP4 yükle. İkisi aynı anda olmaz.</p>
          <Controller control={control} name="cover_video"
            render={({ field }) => (
              <DropZone
                value={field.value}
                onFile={async (file) => {
                  setCoverVidUp(true);
                  try { field.onChange(await upload(file, folder)); }
                  catch (e) { setSaveMsg({ ok: false, text: translateError(e) }); }
                  setCoverVidUp(false);
                }}
                onClear={() => field.onChange("")}
                uploading={coverVidUp}
                accept="video/mp4"
                hint="MP4 — veya aşağıya YouTube URL girin"
              />
            )}
          />
          <div>
            <Lbl>YouTube URL veya MP4 yolu</Lbl>
            <input {...register("cover_video")} className={inp}
              placeholder="https://www.youtube.com/watch?v=VIDEO_ID" />
            <Warn text={youtubeWarning(coverVid)} />
            {coverVid && isYouTubeUrl(coverVid) && !youtubeWarning(coverVid) && (
              <p className="text-[#333] text-xs mt-1">
                Embed: {normalizeYouTubeUrl(coverVid)}
              </p>
            )}
          </div>

          <SecTitle>Kapak Video Poster (opsiyonel)</SecTitle>
          <p className="text-[#333] text-xs -mt-2">
            Video yüklenene kadar veya autoplay bloklandığında gösterilir.
          </p>
          <Controller control={control} name="cover_video_poster"
            render={({ field }) => {
              const hasImg = field.value && isImgUrl(field.value);
              return (
                <div>
                  <div className="relative w-full bg-[#111] border border-white/8" style={{ paddingTop: "37.5%" }}>
                    <div className="absolute inset-0">
                      {hasImg ? (
                        <>
                          <img
                            src={field.value}
                            alt=""
                            className="w-full h-full object-cover"
                            onError={e => { e.target.style.display = "none"; }}
                          />
                          <button type="button" onClick={() => field.onChange("")}
                            className="absolute top-1.5 right-1.5 w-6 h-6 bg-black/70 flex items-center justify-center hover:bg-red-500/80 transition-colors z-10">
                            <X size={10} />
                          </button>
                        </>
                      ) : (
                        <label className="flex flex-col items-center justify-center h-full cursor-pointer hover:bg-white/2 transition-colors">
                          {coverVidPosterUp
                            ? <Loader2 size={16} className="text-[#C5A059] animate-spin" />
                            : <Upload size={16} className="text-[#333]" />}
                          <span className="text-xs text-[#444] mt-1">
                            {coverVidPosterUp ? "Yükleniyor..." : "Poster görseli"}
                          </span>
                          <input type="file" accept="image/*" className="sr-only"
                            onChange={async e => {
                              const f = e.target.files[0];
                              if (!f) return;
                              setCoverVidPosterUp(true);
                              try { field.onChange(await upload(f, folder)); }
                              catch (err) { console.error(err); }
                              setCoverVidPosterUp(false);
                              e.target.value = "";
                            }} />
                        </label>
                      )}
                    </div>
                  </div>
                  <div className="mt-2">
                    <Lbl>Veya URL</Lbl>
                    <input {...register("cover_video_poster")} className={inp} placeholder="/images/projeler/poster.png" />
                  </div>
                </div>
              );
            }}
          />
        </div>
      );

      case "galeri": return (
        <div className="space-y-3">
          <SecTitle>Galeri ({galItems.length} öğe)</SecTitle>

          {/* Bulk upload */}
          <label className={`flex items-center justify-center gap-2 py-5 border border-dashed cursor-pointer transition-colors ${
            bulkUploading ? "border-[#C5A059] bg-[#C5A059]/5" : "border-white/8 hover:border-white/15"
          }`}>
            {bulkUploading
              ? <Loader2 size={15} className="text-[#C5A059] animate-spin" />
              : <Upload size={15} className="text-[#333]" />}
            <span className="text-sm text-[#444]">
              {bulkUploading
                ? `Yükleniyor... ${bulkProgress.done}/${bulkProgress.total}`
                : "Çoklu görsel yükle"}
            </span>
            <span className="text-xs text-[#2a2a2a]">PNG, JPG, WebP, MP4</span>
            <input ref={bulkRef} type="file" multiple accept="image/*,video/mp4"
              className="sr-only" onChange={handleBulkUpload} />
          </label>

          {/* Gallery items */}
          <DndContext sensors={sensors} collisionDetection={closestCenter}
            onDragEnd={e => endDrag(e, galItems, movGal)}>
            <SortableContext items={galItems.map(f => f.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-1.5">
                {galItems.map((item, i) => {
                  const srcVal  = watch(`gallery.${i}.src`) || "";
                  const typeVal = watch(`gallery.${i}.type`) || "image";
                  const ytWarn  = typeVal === "youtube" ? youtubeWarning(srcVal) : null;
                  return (
                    <DragItem key={item.id} id={item.id}>
                      <div className="bg-[#0d0d0d] border border-white/5 p-2">
                        {/* Row 1: thumbnail + main fields */}
                        <div className="grid grid-cols-[48px_1fr_72px_auto] gap-2 items-center">
                          {/* thumbnail */}
                          <div className="w-12 h-8 bg-[#111] flex-shrink-0 overflow-hidden">
                            {srcVal && isImgUrl(srcVal)
                              ? <img src={srcVal} alt="" className="w-full h-full object-cover" />
                              : srcVal && (isYouTubeUrl(srcVal) || typeVal === "youtube")
                              ? <div className="w-full h-full flex items-center justify-center">
                                  <span className="text-red-400 text-[9px]">▶ YT</span>
                                </div>
                              : srcVal
                              ? <div className="w-full h-full flex items-center justify-center">
                                  <Video size={10} className="text-[#555]" />
                                </div>
                              : <div className="w-full h-full" />
                            }
                          </div>
                          {/* src */}
                          <div>
                            <input {...register(`gallery.${i}.src`)} className={`${inp} text-xs`}
                              placeholder="/images/... veya youtube.com/watch?v=..." />
                            {ytWarn && (
                              <div className="flex items-center gap-1 mt-0.5 text-amber-400 text-[10px]">
                                <AlertTriangle size={9} /> {ytWarn}
                              </div>
                            )}
                          </div>
                          {/* type */}
                          <select {...register(`gallery.${i}.type`)} className={`${sel} text-xs`}>
                            <option value="image">Görsel</option>
                            <option value="youtube">YouTube</option>
                            <option value="video">MP4</option>
                          </select>
                          {/* delete */}
                          <button type="button" onClick={() => remGal(i)}
                            className="p-1 text-[#333] hover:text-red-400 transition-colors">
                            <Trash2 size={12} />
                          </button>
                        </div>
                        {/* Row 2: poster/thumb (only for video types) + caption */}
                        {(typeVal === "youtube" || typeVal === "video") && (
                          <div className="grid grid-cols-2 gap-2 mt-1.5">
                            <div>
                              <p className="text-[9px] text-[#333] mb-0.5">Poster URL</p>
                              <input {...register(`gallery.${i}.poster`)} className={`${inp} text-xs`} placeholder="images/kapak.png" />
                            </div>
                            <div>
                              <p className="text-[9px] text-[#333] mb-0.5">Thumb URL</p>
                              <input {...register(`gallery.${i}.thumb`)} className={`${inp} text-xs`} placeholder="images/kapak.png" />
                            </div>
                          </div>
                        )}
                        {/* caption */}
                        <div className="mt-1.5">
                          <input {...register(`gallery.${i}.caption`)} className={`${inp} text-xs`}
                            placeholder="Açıklama (opsiyonel)" />
                        </div>
                      </div>
                    </DragItem>
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>

          <button type="button"
            onClick={() => addGal({ type: "image", src: "", poster: "", thumb: "", caption: "" })}
            className="flex items-center gap-1.5 text-xs text-[#444] hover:text-[#C5A059] transition-colors">
            <Plus size={12} /> Elle Ekle
          </button>
        </div>
      );

      case "ozel": return (
        <div className="space-y-3">
          <SecTitle>Özellikler</SecTitle>
          <DndContext sensors={sensors} collisionDetection={closestCenter}
            onDragEnd={e => endDrag(e, feats, movFeat)}>
            <SortableContext items={feats.map(f => f.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-1.5">
                {feats.map((f, i) => (
                  <DragItem key={f.id} id={f.id}>
                    <div className="flex items-center gap-1.5">
                      <input {...register(`features.${i}.value`)} className={`${inp} flex-1 text-sm`}
                        placeholder="Isı yalıtımlı çatı sistemi" />
                      <button type="button" onClick={() => remFeat(i)}
                        className="p-1 text-[#333] hover:text-red-400 transition-colors flex-shrink-0">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </DragItem>
                ))}
              </div>
            </SortableContext>
          </DndContext>
          <button type="button" onClick={() => addFeat({ value: "" })}
            className="flex items-center gap-1.5 text-xs text-[#444] hover:text-[#C5A059] transition-colors">
            <Plus size={12} /> Özellik Ekle
          </button>
        </div>
      );

      case "malz": return (
        <div className="space-y-3">
          <SecTitle>Malzeme & Markalar</SecTitle>
          <DndContext sensors={sensors} collisionDetection={closestCenter}
            onDragEnd={e => endDrag(e, mats, movMat)}>
            <SortableContext items={mats.map(m => m.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-1.5">
                {mats.map((m, i) => (
                  <DragItem key={m.id} id={m.id}>
                    <div className="grid grid-cols-[1fr_2fr_auto] gap-2 items-center bg-[#0d0d0d] border border-white/5 px-2 py-1.5">
                      <input {...register(`materialHighlights.${i}.title`)} className={`${inp} text-xs`}
                        placeholder="Bosch Ev Aletleri" />
                      <input {...register(`materialHighlights.${i}.description`)} className={`${inp} text-xs`}
                        placeholder="Enerji verimli ankastre beyaz eşyalar." />
                      <button type="button" onClick={() => remMat(i)}
                        className="p-1 text-[#333] hover:text-red-400 transition-colors">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </DragItem>
                ))}
              </div>
            </SortableContext>
          </DndContext>
          <button type="button" onClick={() => addMat({ title: "", description: "" })}
            className="flex items-center gap-1.5 text-xs text-[#444] hover:text-[#C5A059] transition-colors">
            <Plus size={12} /> Malzeme Ekle
          </button>
        </div>
      );

      case "teknik": return (
        <div className="space-y-3">
          <SecTitle>Teknik Detaylar</SecTitle>
          <p className="text-[#333] text-xs -mt-1">Boş bırakılan alanlar detay sayfasında gösterilmez.</p>
          <div className="grid grid-cols-2 gap-3">
            <div><Lbl>İnşaat Alanı</Lbl><input {...register("technicalDetails.constructionArea")} className={inp} placeholder="3.000 m²" /></div>
            <div><Lbl>Yeşil Alan</Lbl><input {...register("technicalDetails.greenArea")} className={inp} placeholder="4.000 m²" /></div>
            <div><Lbl>Blok / Villa Sayısı</Lbl><input {...register("technicalDetails.blocks")} className={inp} placeholder="8 Villa" /></div>
            <div><Lbl>Ticari Alan</Lbl><input {...register("technicalDetails.shopCount")} className={inp} placeholder="10 Ticari Alan" /></div>
            <div><Lbl>Kat Sayısı</Lbl><input {...register("technicalDetails.floors")} className={inp} placeholder="2 Kat" /></div>
            <div><Lbl>Teslim</Lbl><input {...register("technicalDetails.delivery")} className={inp} placeholder="Teslim Edildi - Mayıs 2026" /></div>
          </div>
        </div>
      );

      case "yakin": return (
        <div className="space-y-3">
          <SecTitle>Yakın Çevre</SecTitle>
          <DndContext sensors={sensors} collisionDetection={closestCenter}
            onDragEnd={e => endDrag(e, nearbs, movNear)}>
            <SortableContext items={nearbs.map(n => n.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-1.5">
                {nearbs.map((n, i) => (
                  <DragItem key={n.id} id={n.id}>
                    <div className="flex items-center gap-1.5">
                      <input {...register(`nearby.${i}.value`)} className={`${inp} flex-1 text-sm`}
                        placeholder="Meram Tıp Hastanesi - 1.3 km" />
                      <button type="button" onClick={() => remNear(i)}
                        className="p-1 text-[#333] hover:text-red-400 transition-colors flex-shrink-0">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </DragItem>
                ))}
              </div>
            </SortableContext>
          </DndContext>
          <button type="button" onClick={() => addNear({ value: "" })}
            className="flex items-center gap-1.5 text-xs text-[#444] hover:text-[#C5A059] transition-colors">
            <Plus size={12} /> Ekle
          </button>
        </div>
      );

      case "kat": return (
        <div className="space-y-3">
          <SecTitle>Kat Planları</SecTitle>
          <DndContext sensors={sensors} collisionDetection={closestCenter}
            onDragEnd={e => endDrag(e, fpGroups, movFpGroup)}>
            <SortableContext items={fpGroups.map(g => g.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {fpGroups.map((g, gi) => (
                  <DragItem key={g.id} id={g.id}>
                    <FloorPlanGroup
                      gi={gi}
                      control={control}
                      register={register}
                      watch={watch}
                      setValue={setValue}
                      removeGroup={() => remFpGroup(gi)}
                      folder={folder}
                    />
                  </DragItem>
                ))}
              </div>
            </SortableContext>
          </DndContext>
          <button type="button" onClick={() => addFpGroup({ typeTitle: "", plans: [] })}
            className="flex items-center gap-1.5 text-xs text-[#444] hover:text-[#C5A059] transition-colors">
            <Plus size={12} /> Tip Grubu Ekle
          </button>
        </div>
      );

      case "seo": return (
        <div className="space-y-3">
          <SecTitle>SEO</SecTitle>
          <div>
            <Lbl>SEO Başlığı</Lbl>
            <input {...register("seo_title")} className={inp}
              placeholder="Saklı Bahçe Konya Villa Projesi | Konyap Mühendislik" />
            <p className="text-[#2a2a2a] text-xs mt-1">~60 karakter önerilir.</p>
          </div>
          <div>
            <Lbl>SEO Açıklaması</Lbl>
            <textarea {...register("seo_description")} className={ta} rows={4}
              placeholder="Meram'da butik villa konseptiyle lüks konut projeleri..." />
            <p className="text-[#2a2a2a] text-xs mt-1">~155 karakter önerilir.</p>
          </div>
        </div>
      );

      case "harita": return (
        <div className="space-y-3">
          <SecTitle>Konum (Koordinatlar)</SecTitle>
          <div className="grid grid-cols-2 gap-3">
            <div><Lbl>Enlem (Lat)</Lbl><input type="number" step="any" {...register("lat")} className={inp} placeholder="37.88800" /></div>
            <div><Lbl>Boylam (Lng)</Lbl><input type="number" step="any" {...register("lng")} className={inp} placeholder="32.43447" /></div>
          </div>
          <p className="text-[#2a2a2a] text-xs">
            Google Maps → projeyi bulun → sağ tıklayın → koordinatları kopyalayın.
          </p>
        </div>
      );

      default: return null;
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-3xl pb-24">

      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <Link to="/admin/projeler" className="text-[#444] hover:text-[#C5A059] transition-colors">
          <ArrowLeft size={17} strokeWidth={1.5} />
        </Link>
        <div>
          <p className="text-[#C5A059] text-[10px] tracking-[0.25em] uppercase mb-0.5">
            {isNew ? "Yeni Proje" : "Proje Düzenle"}
          </p>
          <h1 className="text-[#F5F5F7] text-xl font-light">{isNew ? "Proje Ekle" : (watch("title") || "—")}</h1>
        </div>
      </div>

      {/* Tab nav */}
      <div className="flex overflow-x-auto border-b border-white/5 mb-4" style={{ scrollbarWidth: "none" }}>
        {TABS.map(t => (
          <button
            key={t.id} type="button" onClick={() => setTab(t.id)}
            className={`px-3.5 py-2 text-xs font-light tracking-wide whitespace-nowrap flex-shrink-0 border-b-2 -mb-px transition-colors ${
              tab === t.id
                ? "text-[#C5A059] border-[#C5A059]"
                : "text-[#444] border-transparent hover:text-[#666]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <form id="edit-form" onSubmit={handleSubmit(onSubmit)}>
        <div className="bg-[#0a0a0a] border border-white/5 p-5">
          {renderTab()}
        </div>
      </form>

      {/* Fixed save bar */}
      <div className="fixed bottom-0 left-60 right-0 z-50 bg-[#0a0a0a]/95 border-t border-white/8 px-6 py-3 flex items-center justify-between backdrop-blur-sm">
        <div className="flex items-center gap-2 text-sm">
          {saveMsg?.ok === false && (
            <><AlertCircle size={13} className="text-red-400" /><span className="text-red-400 text-xs">{saveMsg.text}</span></>
          )}
          {saveMsg?.ok === true && (
            <><Check size={13} className="text-green-400" /><span className="text-green-400 text-xs">{saveMsg.text}</span></>
          )}
        </div>
        <div className="flex items-center gap-4">
          <Link to="/admin/projeler" className="text-xs text-[#444] hover:text-[#F5F5F7] transition-colors">
            İptal
          </Link>
          <button
            type="submit" form="edit-form" disabled={saving}
            className="bg-[#C5A059] hover:bg-[#D4AF37] disabled:opacity-50 text-black px-7 py-2 text-[11px] font-semibold tracking-widest uppercase transition-colors flex items-center gap-1.5"
          >
            {saving && <Loader2 size={12} className="animate-spin" />}
            {saving ? "Kaydediliyor..." : isNew ? "Oluştur" : "Kaydet"}
          </button>
        </div>
      </div>

    </div>
  );
};

export default AdminProjectEdit;
